import * as ng from '@angular/core';
import * as Rx from 'rxjs';
import * as Hls from 'hls.js';
import {Player, PlayerStatus} from "./player";
import {buildQuery} from "../query";
import {catchError, map} from "rxjs/operators";

export class PlayerHLS implements Player {

  private hls: Hls;

  private _playListURL: string;

  status: PlayerStatus = "unloaded";

  private play$: Rx.Subscription;

  constructor(
    private video: ng.ElementRef<HTMLVideoElement>
  ) {
  }

  canPause(): boolean {
    return this.status === 'played';
  }

  pause(force: boolean): boolean {
    if (!this.canPause() && !force) {
      return false;
    }
    if (this.play$ !== undefined) {
      this.play$.unsubscribe();
      this.play$ = undefined;
    }
    this.video.nativeElement.pause();
    return true;
  }

  play(playListURL: string, query: { [_: string]: string }, forceReset: boolean) {
    if (this._playListURL === playListURL && !forceReset) {
      return;
    }

    this._playListURL = playListURL;
    this.hls = new Hls(hlsConfig);
    this.hls.attachMedia(this.video.nativeElement);
    // queryは必要なさそうなので一旦コメントアウト。問題あれば調査。index.m3u8ファイルだけではなく「*」許可になればqueryを後ろにつけても問題はないと思われる。
    // this.hls.loadSource(playListURL + '?' + buildQuery(query));
    this.hls.loadSource(playListURL);

    this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
      this.executePlay();
    });

    this.hls.on(Hls.Events.FRAG_LOADED, () => {
      if (this.hls.media.paused) {
        this.executePlay();
      }
    });

    this.hls.on(Hls.Events.ERROR, (_kind, data) => {
      if (!data.fatal) {
        console.warn(_kind, data);
        return;
      }
      console.error(_kind, data);
      this.destroy();
      if (data.response !== undefined && data.response.code === 403) {
        const response = JSON.parse(decodeURIComponent(data.networkDetails.getResponseHeader('x-webhook-reply')));
        if (response && response["errorCode"] === 'INSUFFICIENT_LIVE_CONDITION') {
          this.status = "cannot_view";
          return;
        }
      }
      this.play(playListURL, query, true) // 不明なエラーなのでリトライ
    });

    this.status = "loading";
  }

  stopLoading(): boolean {
    if (!this.hls || !this.video.nativeElement) {
      return;
    }

    if (this.play$ !== undefined) {
      this.play$.unsubscribe();
      this.play$ = undefined;
    }

    this.status = "unloaded";
    if (!this.pause(false)) {
      return false;
    }
    this.hls.stopLoad();
    return true;
  }

  destroy() {
    this.video.nativeElement.muted = true;
    this.video.nativeElement.src = "";

    if (this.play$ !== undefined) {
      this.play$.unsubscribe();
      this.play$ = undefined;
    }

    if (this.hls && this.video.nativeElement) {
      this.hls.detachMedia();
      this.hls.destroy();
    }

    this.hls = null;
  }

  private executePlay() {
    if (this.play$) {
      this.play$.unsubscribe();
      this.play$ = undefined;
    }

    let playAsync = this.video.nativeElement.play();
    if (playAsync === undefined) {
      // "video.play" on IE 11 returns undefined.
      playAsync = Promise.resolve()
    }

    // MEMO: https://stackoverflow.com/a/50742427
    if (this.hls.media) {
      this.hls.media.muted = true;
    }

    this.play$ = Rx.from(playAsync).pipe(
      map(() => {
        this.status = "played";
      }),
      catchError(_e => {
        const e = _e as Error;
        console.error(e.name, e.message, e.stack);
        throw e;
      })
    ).subscribe();
  }
}

const hlsConfig: Partial<Hls.Config> = {
  fragLoadingMaxRetry: 30,
  // debug: true,
  // cokkie送るための設定
  xhrSetup: function(xhr, url) {
    xhr.withCredentials = true;
    xhr.setRequestHeader("Access-Control-Allow-Headers","Content-Type, Accept, X-Requested-With");
    xhr.setRequestHeader('Access-Control-Allow-Credentials', 'true');
  },
  // 以下も必要になるかなぁ・・・
  fetchSetup: (context, initParams) => {
    initParams.credentials = 'include';
    return new Request(context.url, initParams);
  },
};
